/**
 * Chat Repository - All API calls for the chatbot service
 * Following the repository pattern from CLAUDE.md
 */

import { supabase } from "@/integrations/supabase/client";
import { getLanguageStatic, getTranslationsStatic } from "@/lib/i18n/LanguageContext";
import { fill } from "@/lib/i18n/fill";
import { resolveChatbotBaseUrl } from "@/lib/chatbotBaseUrl";
import { withAbort } from "./abort";

async function sessionForRequest(signal?: AbortSignal) {
  signal?.throwIfAborted();
  const result = await withAbort<Awaited<ReturnType<typeof supabase.auth.getSession>>>(
    supabase.auth.getSession(),
    signal
  );
  signal?.throwIfAborted();
  return result;
}

// Base URL from environment (e.g., http://localhost:8010 or https://chat-dev.franklin.ee), or
// the dev-only ChatbotSwitcher override. Resolved once at module load, exactly like the Supabase
// branch override — the switcher reloads the page after changing it.
const CHATBOT_BASE_URL = resolveChatbotBaseUrl();

// API paths (constant across all environments)
const CHATBOT_PATHS = {
  queryStream: "/chatbot/query/stream",
  queryAnswer: "/chatbot/query/answer",
  session: "/chatbot/session",
  sessions: "/chatbot/sessions",
  messages: "/chatbot/messages",
} as const;

export interface ChatMessage {
  id?: string | null;
  role: "user" | "assistant";
  content: string;
}

export interface SendMessageParams {
  message: string;
  threadId: string;
  orgId: string;
  buildingId: number;
  userId?: string;
  enableWebSearch?: boolean; // WHA-2716: Web search toggle
  projectId?: number; // WHA-3829: "Work in a project" scope
}

export interface ChatResponse {
  message?: string;
  response?: string;
  generated_title?: string | null;
  assistant_message_id?: string | null;
  source_filenames?: string[] | null;
}

/**
 * A document the chatbot generated during a turn (e.g. a PDF) that should
 * be surfaced to the user as a downloadable artifact (WHA-3118).
 */
export interface GeneratedFile {
  id: string;
  filename: string;
  mime: string;
  size_bytes: number;
  /** Base64-encoded file contents — decoded client-side on download click. */
  data_b64: string;
}

export interface SendMessageResult {
  message: string;
  generatedTitle: string | null;
  assistantMessageId: string | null;
  sourceFilenames: string[];
  generatedFiles: GeneratedFile[];
  choices: string[];
  choiceQuestion: string;
  usedDocumentTool: boolean;
  ehrLink: string | null;
}

export type FeedbackRating = "positive" | "negative";

export interface FeedbackResult {
  id: string;
  messageId: string;
  rating: FeedbackRating;
  comment?: string | null;
  updatedAt?: string | null;
}

export interface SessionResponse {
  session_id?: string;
}

/**
 * Fetch a new session ID from the chatbot service
 *
 * Sends the access token like every other chatbot call: a sandbox chatbot picks its
 * database from the token's `iss` claim, so an unauthenticated call has nothing to
 * route on and cannot be served at all.
 *
 * @throws {Error} If the request fails or session_id is missing
 */
export async function fetchSession(): Promise<string> {
  if (!CHATBOT_BASE_URL) {
    throw new Error("Chatbot URL is not configured. Set VITE_CHATBOT_URL to continue.");
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Authentication required");
  }

  const response = await fetch(`${CHATBOT_BASE_URL}${CHATBOT_PATHS.session}`, {
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch chat session: ${response.status} ${response.statusText}`);
  }

  const data = (await response.json()) as SessionResponse;

  if (!data.session_id) {
    throw new Error("Session ID missing from response");
  }

  return data.session_id;
}

// ============================================================================
// SSE Streaming Types & Functions
// ============================================================================

/**
 * What ticking "Always allow" on a confirmation card means: `persistent` saves
 * a preference that later chats honour; `reply` covers only the rest of the
 * current reply, for tools (or changes, e.g. a date edit that moves a
 * schedule) the chatbot never auto-approves across chats. Absent on payloads
 * from a chatbot that predates it — treat as persistent.
 */
export type AlwaysAllowScope = "persistent" | "reply";

/**
 * Discriminated union — the chatbot stamps every confirmation payload with a
 * `tool_name` so the frontend can pick the right sub-view. Payloads with a
 * missing/unrecognized `tool_name` route to the UnknownToolView fallback in
 * BuildingDataConfirmationCard.tsx (WHA-3343) — we no longer silently fall
 * back to the building-data renderer, which was producing empty cards when
 * the chatbot and frontend versions drifted.
 */
export type ToolConfirmationPayload = { always_allow_scope?: AlwaysAllowScope } & (
  | ({ tool_name: "update_building_data" } & UpdateBuildingDataConfirmation)
  | ({ tool_name: "create_pm_issue" } & CreatePMIssueConfirmation)
  | ({ tool_name: "update_pm_issue" } & UpdatePMIssueConfirmation)
  | ({ tool_name: "add_pm_issue_comment" } & AddPMCommentConfirmation)
  | ({ tool_name: "add_file_to_folder" } & AddFileToFolderConfirmation)
  | ({ tool_name: "set_document_verified" } & SetDocumentVerifiedConfirmation)
  | ({
      tool_name: "update_documents" | "post_comment" | "update_contact" | "update_brief";
    } & ChatChangeConfirmation)
  | ({
      tool_name:
        | "franklin_project_change"
        | "franklin_share_change"
        | "franklin_document_change"
        | "franklin_contact_change"
        | "franklin_brief_change"
        | "franklin_folder_change"
        | "franklin_planning_change"
        | "franklin_collaboration_change"
        | "franklin_profile_change"
        | "franklin_building_change"
        | "franklin_member_change"
        | "franklin_organization_change";
      tool_label: string;
      summary: string;
    } & Record<string, unknown>)
);

/**
 * The chatbot's document, folder, discussion, contact and brief changes. The
 * chatbot writes the card itself from the names the user used — a headline and
 * one plain line per change — so the app renders it as given, with no ids.
 */
export interface ChatChangeConfirmation {
  tool_label: string;
  summary: string;
  details: string[];
}

/**
 * The chatbot marking a document Verified, or clearing the mark. The backend
 * stamps who and when itself, so the card only has to state which document and
 * which direction — there is nothing here for the user to adjust, and no
 * overrides flow back.
 */
export interface SetDocumentVerifiedConfirmation {
  filename: string;
  verified: boolean;
  current_state: "verified" | "unverified";
  proposed_state: "verified" | "unverified";
}

/**
 * WHA-3354: `add_file_to_folder` lets the chatbot copy a document the user
 * already sees (a chat attachment, a generated PDF, or an existing project
 * doc) into a regular project folder. The user may switch the target inside
 * the card before approving — the override flows back via
 * `submitToolConfirmation(..., overrides)`.
 */
export interface AddFileToFolderConfirmation {
  source: {
    document_id: number;
    filename: string;
  };
  target: {
    project_id: number;
    project_name: string;
    folder_id: number | null;
    folder_path: string | null;
  };
  building_id: number;
}

export interface UpdateBuildingDataConfirmation {
  attribute_key: string;
  attribute_label: string;
  attribute_unit: string | null;
  current_value: string | null;
  proposed_value: string;
  reasoning: string;
}

interface PMUserSummary {
  first_name: string | null;
  last_name: string | null;
  email: string;
}

interface PMDisciplineSummary {
  name: string;
  abbreviation: string | null;
}

interface PMParentIssueSummary {
  issue_number: number;
  title: string;
}

export interface CreatePMIssueConfirmation {
  tool_label: string;
  summary: string;
  issue: {
    title: string;
    status: "in_progress" | "resolved" | "closed";
    start_date: string | null;
    due_date: string | null;
    project: string | null;
    disciplines: PMDisciplineSummary[];
    assignees: PMUserSummary[];
    linked_documents: string[];
    parent_issue: PMParentIssueSummary | null;
  };
}

export type PMIssueDiff =
  | {
      field: "title" | "status" | "project";
      current: string | null;
      proposed: string | null;
    }
  | { field: "start_date"; current: string | null; proposed: string | null }
  | { field: "due_date"; current: string | null; proposed: string | null }
  | { field: "disciplines"; current: PMDisciplineSummary[]; proposed: PMDisciplineSummary[] }
  | { field: "assignees"; current: PMUserSummary[]; proposed: PMUserSummary[] }
  | { field: "linked_documents"; current: null; proposed: { add: string[]; remove: string[] } }
  | {
      field: "parent_issue";
      current: PMParentIssueSummary | null;
      proposed: PMParentIssueSummary | null;
    }
  | { field: "schedule_pinned"; current: boolean; proposed: boolean }
  | { field: "dependencies"; current: null; proposed: { add: string[]; remove: string[] } }
  | {
      field: "checklist";
      current: null;
      proposed: { add: string[]; update: string[]; remove: string[]; reorder: string[] };
    };

/** One issue's dates before and after, as the scheduling engine computed them. */
export interface PMScheduleMove {
  issue_number: number;
  title: string;
  old_start: string | null;
  old_due: string | null;
  new_start: string | null;
  new_due: string | null;
}

/**
 * A scheduler warning. `pinned`: a hand-scheduled issue will not move with the
 * change. `soft_violation`: the change breaks a Flexible link, so the issue
 * would start before `required_start`. Other kinds may arrive from a newer
 * scheduler and are still shown.
 */
export interface PMScheduleWarning {
  kind: string;
  issue_number?: number;
  title?: string;
  predecessor_issue_number?: number;
  required_start?: string | null;
  current_start?: string | null;
}

export interface PMScheduleCompletion {
  old?: string | null;
  new?: string | null;
  delta_days?: number | null;
}

/** What a date change moves; `moved` is a sample, `moved_total` the count. */
export interface PMScheduleEffect {
  moved: PMScheduleMove[];
  moved_total: number;
  warnings: PMScheduleWarning[];
  completion: PMScheduleCompletion;
}

/** A whole-project change: many issues' dates, links and baselines in one card. */
export interface PMScheduleBatch extends PMScheduleEffect {
  project: string | null;
  cascade: boolean;
  targets: PMScheduleMove[];
  dependencies: { add: string[]; update: string[]; remove: string[] };
  baseline_save?: string;
  baseline_delete?: string;
}

export interface UpdatePMIssueConfirmation {
  tool_label: string;
  summary: string;
  /** Absent for a whole-project change, which names no single issue. */
  issue_number?: number | null;
  diff: PMIssueDiff[];
  schedule?: PMScheduleEffect;
  batch?: PMScheduleBatch;
}

export interface AddPMCommentConfirmation {
  tool_label: string;
  summary: string;
  issue_number: number;
  body: string;
  mentions: PMUserSummary[];
}

export type StreamEvent =
  | {
      type: "status";
      message: string;
      /** Catalog key + params behind `message` (chatbot/statuses.py). Absent on
       *  servers older than the key-bearing status payload; the UI falls back to
       *  parsing `message`. */
      key?: string | null;
      params?: Record<string, unknown> | null;
    }
  | {
      type: "intermediate_output";
      message: string;
    }
  | {
      type: "complete";
      message: string;
      generated_title?: string | null;
      assistant_message_id?: string | null;
      source_filenames?: string[] | null;
      generated_files?: GeneratedFile[] | null;
      choices?: string[] | null;
      choice_question?: string | null;
      used_document_tool?: boolean;
      ehr_link?: string | null;
    }
  | {
      type: "ask_user";
      question_id: string;
      questions: Array<{
        question: string;
        header?: string;
        options: Array<{ label: string; description?: string }>;
        multiSelect?: boolean;
      }>;
    }
  | {
      type: "tool_confirmation";
      question_id: string;
      payload: ToolConfirmationPayload;
      /** Which chatbot pod is holding the waiting coroutine. The backend's
       *  pending-confirmation registry is per-process, so the answer POST has
       *  to come back to that pod; we echo this as `?route=` and nginx proxies
       *  on it (chatbot `pod_identity.py` + `k8s/nginx-sticky.yaml`, merged in
       *  chatbot#181 — the sticky cookie it replaces was inert cross-site).
       *  Absent on a single-replica deployment and on older servers, and the
       *  request is then simply routeless. */
      server?: string | null;
    }
  | {
      /** Confirmations the SERVER resolved without the user touching the card.
       *  Drop them: left on screen they are buttons whose only outcome is a
       *  404. `auto_approved` is the rest of a tool's queue released when the
       *  user ticks "Always allow" — the siblings are already blocked inside
       *  `wait_for_confirmation` and checked the preference before it existed,
       *  so only the answer endpoint can free them (chatbot#181,
       *  `approve_pending_for_tool`). `cancelled` is a turn that ended. */
      type: "tool_confirmation_resolved";
      question_ids: string[];
      reason: "auto_approved" | "cancelled";
    }
  | {
      type: "building_data_updated";
      attribute_key: string;
    }
  | {
      // WHA-3301: chatbot performed a PM write (create / update / comment).
      // The PM module listens for these and refetches immediately instead of
      // relying solely on Supabase realtime broadcasts (which can drop on
      // postgres-role inserts depending on the publication's writer role).
      type: "pm_issue_changed";
      kind: "created" | "updated" | "commented";
      issue_number: number | null;
      building_id: number | null;
    }
  | {
      type: "error";
      message: string;
    }
  | {
      type: "text_delta";
      text: string;
    };

/**
 * Send a message to the chatbot via SSE streaming endpoint.
 * Emits intermediate status events and returns the final result.
 * @throws {Error} If the request fails or stream ends without a complete event
 */
export async function sendMessageStream(
  params: SendMessageParams,
  onEvent: (event: StreamEvent) => void,
  signal?: AbortSignal
): Promise<SendMessageResult> {
  if (!CHATBOT_BASE_URL) {
    throw new Error("Chatbot URL is not configured. Set VITE_CHATBOT_URL to continue.");
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();

  // Auth teardown may abort while getSession is pending. Never dispatch the
  // old question using a token resolved after that session ended.
  signal?.throwIfAborted();

  if (!session?.access_token) {
    throw new Error("Authentication required");
  }

  const payload = {
    message: params.message,
    thread_id: params.threadId,
    org_id: params.orgId,
    building_id: params.buildingId,
    user_id: params.userId,
    // UI language for the backend's status pills (the answer itself follows
    // the conversation language).
    language: getLanguageStatic(),
    ...(params.enableWebSearch && { enable_web_search: true }),
    ...(params.projectId != null && { project_id: params.projectId }),
  };

  const response = await fetch(`${CHATBOT_BASE_URL}${CHATBOT_PATHS.queryStream}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    let detail = `Chatbot API request failed with status ${response.status}`;
    try {
      const errorBody: unknown = await response.json();
      if (errorBody && typeof errorBody === "object") {
        const body = errorBody as { detail?: unknown; message?: unknown };
        if (body.detail && typeof body.detail === "object") {
          const quota = body.detail as { code?: unknown; limit?: unknown; message?: unknown };
          const copy = getTranslationsStatic().billing.plans;
          if (quota.code === "CHAT_MONTHLY_LIMIT_EXCEEDED") {
            const limit =
              typeof quota.limit === "number" &&
              Number.isSafeInteger(quota.limit) &&
              quota.limit >= 0
                ? quota.limit
                : 10;
            detail = fill(copy.chatLimit, { limit: String(limit) });
          } else if (quota.code === "BILLING_BUILDING_ACTIVATION_PENDING") {
            detail = copy.activationPending;
          } else if (quota.code === "CHAT_QUOTA_UNAVAILABLE") {
            detail = copy.chatUnavailable;
          } else if (typeof quota.message === "string") {
            detail = quota.message;
          }
        } else if (typeof body.detail === "string") {
          detail = body.detail;
        } else if (typeof body.message === "string") {
          detail = body.message;
        }
      }
    } catch {
      // ignore parse errors
    }
    throw new Error(detail);
  }

  if (!response.body) {
    throw new Error("Response body is null — streaming not supported");
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalResult: SendMessageResult | null = null;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // Normalize \r\n to \n for consistent SSE parsing across transports
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
      const parts = buffer.split("\n\n");
      buffer = parts.pop() || "";

      for (const part of parts) {
        // Each SSE event may contain multiple lines; find the data line
        const dataLine = part.split("\n").find((l) => l.startsWith("data: "));
        if (!dataLine) continue;
        try {
          const event: StreamEvent = JSON.parse(dataLine.slice(6));
          onEvent(event);

          if (event.type === "complete") {
            finalResult = {
              message: event.message,
              generatedTitle: event.generated_title ?? null,
              assistantMessageId: event.assistant_message_id ?? null,
              sourceFilenames: event.source_filenames ?? [],
              generatedFiles: event.generated_files ?? [],
              choices: event.choices ?? [],
              choiceQuestion: event.choice_question ?? "",
              usedDocumentTool: event.used_document_tool ?? false,
              ehrLink: event.ehr_link ?? null,
            };
          } else if (event.type === "error") {
            throw new Error(event.message);
          }
        } catch (e) {
          if (e instanceof Error && e.message !== "Unexpected end of JSON input") {
            throw e;
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  if (!finalResult) throw new Error("Stream ended without complete event");
  return finalResult;
}

/**
 * Submit the user's answer to an AskUserQuestion prompt (WHA-2873).
 * Called while the SSE stream is still open — unblocks the backend canUseTool callback.
 */
export async function submitAnswer(
  questionId: string,
  answers: Record<string, string>
): Promise<void> {
  if (!CHATBOT_BASE_URL) {
    throw new Error("Chatbot URL is not configured. Set VITE_CHATBOT_URL to continue.");
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Authentication required");
  }

  const response = await fetch(`${CHATBOT_BASE_URL}${CHATBOT_PATHS.queryAnswer}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      question_id: questionId,
      answers,
    }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody?.detail || `Failed to submit answer: ${response.status}`);
  }
}

/**
 * What the answer POST tells us beyond "it landed": `released` are the sibling
 * confirmations the backend freed because this answer ticked "Always allow"
 * (chatbot#181). They are already answered server-side, so the caller drops
 * their cards rather than POSTing for them — the SSE
 * `tool_confirmation_resolved` event says the same thing, and whichever
 * arrives first is enough.
 */
export type SubmitConfirmationResult = { released: string[] };

/**
 * The answer handler's own 404 — the backend is no longer waiting on this
 * question (cancelled turn, superseded turn, or the HITL timeout), so retrying
 * can never succeed and the caller must retire the card. Matched against the
 * handler's wording (chatbot `api.py`: "Question not found or expired") rather
 * than "any 404 that is not the framework's": a 404 raised in FRONT of the
 * handler — the ingress resolving a `?route=` to a pod that has gone, an edge
 * cache, a rewritten path — must stay retryable, because retiring a card whose
 * coroutine is still waiting strands it for the whole timeout. Failing the
 * other way costs a Try again that 404s once, which is why this errs toward
 * retryable when the backend rewords.
 *
 * For the same reason the wording alone is not enough: only the pod that
 * raised the card can say the question is gone, so this is a genuine expiry
 * ONLY when the POST carried a `?route=`. Answered routeless it round-robins,
 * and every replica that never held the question answers with this exact body
 * while the question is still live — and still holding a turn lock — on
 * another.
 */
const CONFIRMATION_GONE = /not found or expired|no longer pending/i;

export class ConfirmationExpiredError extends Error {
  constructor(questionId: string) {
    super(`Confirmation ${questionId} is no longer pending`);
    this.name = "ConfirmationExpiredError";
  }
}

/**
 * Any other HTTP failure from the answer endpoint, carrying the status.
 *
 * The status tells a 404 that is not the handler's — the ingress unable to
 * resolve `?route=` at all, which a retry must drop the route to get past —
 * from a 502/503/504, which is BOTH a deleted pod (the sticky proxy
 * `proxy_pass`es straight at the route) and a briefly overloaded proxy in
 * front of a pod that is alive and still holding the waiter. So the status
 * cannot decide the second case on its own; the caller unpins a route that
 * fails twice running instead. A transport failure throws a bare TypeError and
 * so carries no status at all.
 */
export class ConfirmationSubmitError extends Error {
  readonly status: number;
  /**
   * The chatbot app itself answered — it sent a JSON `detail`. A 404 raised in
   * FRONT of it (the ingress unable to turn `?route=` into a pod, an edge
   * cache) has no JSON body at all, and that is the only 404 that says the
   * route resolves nowhere: a `detail` proves the request DID reach a pod, so
   * the pin is still good and must survive.
   */
  readonly fromHandler: boolean;
  /**
   * The answer endpoint says this question is gone (see `CONFIRMATION_GONE`).
   * Routed that is a genuine expiry and never reaches here — it is
   * `ConfirmationExpiredError`. Routeless the same body also comes from every
   * replica that never held the question, so the caller decides: retryable
   * once, expired if the retry is told the same thing.
   */
  readonly gone: boolean;

  constructor(
    message: string,
    status: number,
    options?: { fromHandler?: boolean; gone?: boolean }
  ) {
    super(message);
    this.name = "ConfirmationSubmitError";
    this.status = status;
    this.fromHandler = options?.fromHandler ?? false;
    this.gone = options?.gone ?? false;
  }
}

/**
 * Submit the user's approve/deny decision for a chatbot tool confirmation (WHA-3058).
 * Called while the SSE stream is still open — unblocks the backend canUseTool callback.
 */
export async function submitConfirmation(
  questionId: string,
  confirmed: boolean,
  options?: {
    signal?: AbortSignal;
    remember?: boolean;
    rememberToolName?: string;
    /**
     * WHA-3354: tool-arg overrides the user adjusted inside the confirmation
     * card before approving (e.g. switched the destination project / folder
     * for `add_file_to_folder`). Forwarded to the tool body server-side.
     */
    overrides?: Record<string, unknown>;
    /**
     * The pod that raised the card (`server` on the SSE event). The registry
     * holding the waiting coroutine is per-process, so without this the POST
     * round-robins across replicas and resolves nothing.
     */
    route?: string | null;
  }
): Promise<SubmitConfirmationResult> {
  if (!CHATBOT_BASE_URL) {
    throw new Error("Chatbot URL is not configured. Set VITE_CHATBOT_URL to continue.");
  }

  const {
    data: { session },
  } = await sessionForRequest(options?.signal);

  if (!session?.access_token) {
    throw new Error("Authentication required");
  }

  const url = options?.route
    ? `${CHATBOT_BASE_URL}${CHATBOT_PATHS.queryAnswer}?route=${encodeURIComponent(options.route)}`
    : `${CHATBOT_BASE_URL}${CHATBOT_PATHS.queryAnswer}`;

  const response = await fetch(url, {
    signal: options?.signal,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      question_id: questionId,
      confirmed,
      // WHA-3318: persist auto-approve when the user ticks "Always allow"
      // on an approve action. Backend ignores `remember` when confirmed=false.
      ...(options?.remember && options?.rememberToolName
        ? { remember: true, remember_tool_name: options.rememberToolName }
        : {}),
      ...(options?.overrides && Object.keys(options.overrides).length > 0
        ? { overrides: options.overrides }
        : {}),
    }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => null);
    const detail = typeof errorBody?.detail === "string" ? errorBody.detail : null;
    const gone = response.status === 404 && detail !== null && CONFIRMATION_GONE.test(detail);
    if (
      gone &&
      // Routed, so the pod that raised the card is the one saying this.
      // Routeless, the same body ALSO means "not on THIS replica", which says
      // nothing about the pod holding the waiter — so it stays retryable here
      // and is reported as `gone` for the caller to settle.
      Boolean(options?.route)
    ) {
      throw new ConfirmationExpiredError(questionId);
    }
    throw new ConfirmationSubmitError(
      detail || `Failed to submit confirmation: ${response.status}`,
      response.status,
      { fromHandler: detail !== null, gone }
    );
  }

  const body = await response.json().catch(() => null);
  const released = Array.isArray(body?.released)
    ? body.released.filter((id: unknown): id is string => typeof id === "string")
    : [];
  return { released };
}

// ----------------------------------------------------------------------------
// Tool preferences (WHA-3318)
// ----------------------------------------------------------------------------

export interface ChatbotToolPreference {
  tool_name: string;
  auto_approve: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export async function listToolPreferences(): Promise<ChatbotToolPreference[]> {
  if (!CHATBOT_BASE_URL) {
    throw new Error("Chatbot URL is not configured. Set VITE_CHATBOT_URL to continue.");
  }
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error("Authentication required");
  }
  const response = await fetch(`${CHATBOT_BASE_URL}/chatbot/preferences/tools`, {
    method: "GET",
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody?.detail || `Failed to list tool preferences: ${response.status}`);
  }
  const data = await response.json();
  return Array.isArray(data?.preferences) ? data.preferences : [];
}

export async function revokeToolPreference(toolName: string): Promise<void> {
  if (!CHATBOT_BASE_URL) {
    throw new Error("Chatbot URL is not configured. Set VITE_CHATBOT_URL to continue.");
  }
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error("Authentication required");
  }
  const response = await fetch(
    `${CHATBOT_BASE_URL}/chatbot/preferences/tools/${encodeURIComponent(toolName)}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${session.access_token}` },
    }
  );
  if (!response.ok && response.status !== 404) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody?.detail || `Failed to revoke preference: ${response.status}`);
  }
}

/**
 * Set a tool's auto-approve preference on or off (WHA-3652).
 *
 * `true` allowlists the tool so its in-chat confirmation card is skipped;
 * `false` clears it so the card returns. Idempotent on the backend.
 */
export async function setToolPreference(toolName: string, autoApprove: boolean): Promise<void> {
  if (!CHATBOT_BASE_URL) {
    throw new Error("Chatbot URL is not configured. Set VITE_CHATBOT_URL to continue.");
  }
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error("Authentication required");
  }
  const response = await fetch(
    `${CHATBOT_BASE_URL}/chatbot/preferences/tools/${encodeURIComponent(toolName)}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ auto_approve: autoApprove }),
    }
  );
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody?.detail || `Failed to set tool preference: ${response.status}`);
  }
}

/**
 * Generate a client-side fallback session ID
 * Used when the session endpoint is unavailable
 */
export function generateFallbackSessionId(): string {
  return `client-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

// ============================================================================
// Multi-Session Types (WHA-1465)
// ============================================================================

export interface ChatSessionSummary {
  id: string;
  title: string;
  threadId: string;
  createdAt: string;
  lastMessageAt: string | null;
  messageCount: number;
}

export interface CreateSessionResponse {
  id: string;
  title: string;
  threadId: string;
  createdAt: string;
}

export interface UpdateSessionResponse {
  id: string;
  title: string;
}

export interface RollbackSessionResponse {
  success: boolean;
  sessionId: string;
  deletedMessageCount: number;
  rolledBackToMessageId: string | null;
  lastMessageAt: string | null;
}

export interface PrewarmSessionParams {
  signal?: AbortSignal;
  enableWebSearch?: boolean;
  projectId?: number; // WHA-3829: must match what the next query will send
}

export interface PrewarmSessionResponse {
  sessionId: string;
  status: "resumed" | "context_only" | "skipped" | "failed";
  historyReady: boolean;
  reason: string | null;
}

export interface SessionMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
  feedbackRating?: string | null;
  sourceFilenames?: string[];
  choices?: string[];
  choiceQuestion?: string;
  usedDocumentTool?: boolean;
  ehrLink?: string | null;
}

export interface SessionMessagesResponse {
  messages: SessionMessage[];
  total: number;
  hasMore: boolean;
}

/** The chatbot's snake_case wire shapes, before mapping to the types above. */
interface RawChatSession {
  id: string;
  title: string;
  thread_id: string;
  created_at: string;
  last_message_at: string | null;
  message_count: number;
}

interface RawSessionMessage {
  id: string;
  role: SessionMessage["role"];
  content: string;
  created_at: string;
  feedback_rating?: string | null;
  source_filenames?: string[];
  choices?: string[];
  choice_question?: string;
  used_document_tool?: boolean;
  ehr_link?: string | null;
}

interface RawFeedbackResponse {
  id?: string;
  message_id?: string;
  rating?: FeedbackRating;
  comment?: string | null;
  updated_at?: string | null;
  detail?: string;
  message?: string;
}

// ============================================================================
// Multi-Session API Functions (WHA-1465)
// ============================================================================

/**
 * Fetch all active chat sessions for a building
 * @throws {Error} If the request fails
 */
export async function fetchSessions(
  orgId: string,
  buildingId: number,
  signal?: AbortSignal
): Promise<ChatSessionSummary[]> {
  if (!CHATBOT_BASE_URL) {
    throw new Error("Chatbot URL is not configured. Set VITE_CHATBOT_URL to continue.");
  }

  const {
    data: { session },
  } = await sessionForRequest(signal);

  if (!session?.access_token) {
    throw new Error("Authentication required");
  }

  const url = new URL(`${CHATBOT_BASE_URL}${CHATBOT_PATHS.sessions}`);
  url.searchParams.set("org_id", orgId);
  url.searchParams.set("building_id", String(buildingId));

  const response = await fetch(url.toString(), {
    signal,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody?.detail || `Failed to fetch sessions: ${response.status}`);
  }

  const data = await response.json();

  // Transform snake_case to camelCase
  return ((data.sessions as RawChatSession[] | undefined) || []).map((s) => ({
    id: s.id,
    title: s.title,
    threadId: s.thread_id,
    createdAt: s.created_at,
    lastMessageAt: s.last_message_at,
    messageCount: s.message_count,
  }));
}

/**
 * Create a new chat session
 * @throws {Error} If the request fails
 */
export async function createSession(
  orgId: string,
  buildingId: number,
  signal?: AbortSignal
): Promise<CreateSessionResponse> {
  if (!CHATBOT_BASE_URL) {
    throw new Error("Chatbot URL is not configured. Set VITE_CHATBOT_URL to continue.");
  }

  const {
    data: { session },
  } = await sessionForRequest(signal);

  if (!session?.access_token) {
    throw new Error("Authentication required");
  }

  const response = await fetch(`${CHATBOT_BASE_URL}${CHATBOT_PATHS.sessions}`, {
    signal: signal,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      org_id: orgId,
      building_id: buildingId,
    }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody?.detail || `Failed to create session: ${response.status}`);
  }

  const data = await response.json();

  return {
    id: data.id,
    title: data.title,
    threadId: data.thread_id,
    createdAt: data.created_at,
  };
}

/**
 * Archive a chat session (soft delete)
 * @throws {Error} If the request fails
 */
export async function archiveSession(sessionId: string): Promise<void> {
  if (!CHATBOT_BASE_URL) {
    throw new Error("Chatbot URL is not configured. Set VITE_CHATBOT_URL to continue.");
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Authentication required");
  }

  const response = await fetch(
    `${CHATBOT_BASE_URL}${CHATBOT_PATHS.sessions}/${sessionId}/archive`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    }
  );

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody?.detail || `Failed to archive session: ${response.status}`);
  }
}

/**
 * Update a chat session title
 * @throws {Error} If the request fails
 */
export async function renameSession(
  sessionId: string,
  title: string
): Promise<UpdateSessionResponse> {
  if (!CHATBOT_BASE_URL) {
    throw new Error("Chatbot URL is not configured. Set VITE_CHATBOT_URL to continue.");
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Authentication required");
  }

  const response = await fetch(`${CHATBOT_BASE_URL}${CHATBOT_PATHS.sessions}/${sessionId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ title }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody?.detail || `Failed to rename session: ${response.status}`);
  }

  const data = await response.json();

  return {
    id: data.id,
    title: data.title,
  };
}

/**
 * Roll back a chat session to the last successful assistant turn
 * @throws {Error} If the request fails
 */
export interface DeleteExchangeResponse {
  success: boolean;
  sessionId: string;
  deletedMessageCount: number;
  lastMessageAt: string | null;
}

/**
 * Delete the last user+assistant exchange from a session (WHA-2637 edit-message)
 * @throws {Error} If the request fails
 */
export async function deleteLastExchange(sessionId: string): Promise<DeleteExchangeResponse> {
  if (!CHATBOT_BASE_URL) {
    throw new Error("Chatbot URL is not configured. Set VITE_CHATBOT_URL to continue.");
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Authentication required");
  }

  const response = await fetch(
    `${CHATBOT_BASE_URL}${CHATBOT_PATHS.sessions}/${sessionId}/delete-last-exchange`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    }
  );

  const responseBody = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(responseBody?.detail || `Failed to delete last exchange: ${response.status}`);
  }

  return {
    success: Boolean(responseBody?.success),
    sessionId: responseBody?.session_id ?? sessionId,
    deletedMessageCount: Number(responseBody?.deleted_message_count ?? 0),
    lastMessageAt: responseBody?.last_message_at ?? null,
  };
}

/**
 * Roll back a chat session to the last successful assistant turn
 * @throws {Error} If the request fails
 */
export async function rollbackSession(
  sessionId: string,
  signal?: AbortSignal
): Promise<RollbackSessionResponse> {
  if (!CHATBOT_BASE_URL) {
    throw new Error("Chatbot URL is not configured. Set VITE_CHATBOT_URL to continue.");
  }

  const {
    data: { session },
  } = await sessionForRequest(signal);

  if (!session?.access_token) {
    throw new Error("Authentication required");
  }

  const response = await fetch(
    `${CHATBOT_BASE_URL}${CHATBOT_PATHS.sessions}/${sessionId}/rollback`,
    {
      method: "POST",
      signal,
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    }
  );

  const responseBody = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(responseBody?.detail || `Failed to rollback session: ${response.status}`);
  }

  return {
    success: Boolean(responseBody?.success),
    sessionId: responseBody?.session_id ?? sessionId,
    deletedMessageCount: Number(responseBody?.deleted_message_count ?? 0),
    rolledBackToMessageId: responseBody?.rolled_back_to_message_id ?? null,
    lastMessageAt: responseBody?.last_message_at ?? null,
  };
}

/**
 * Prewarm an existing chat session so Claude can resume before the next user query.
 * @throws {Error} If the request fails
 */
export async function prewarmSession(
  sessionId: string,
  params: PrewarmSessionParams = {}
): Promise<PrewarmSessionResponse> {
  if (!CHATBOT_BASE_URL) {
    throw new Error("Chatbot URL is not configured. Set VITE_CHATBOT_URL to continue.");
  }

  const {
    data: { session },
  } = await sessionForRequest(params.signal);

  if (!session?.access_token) {
    throw new Error("Authentication required");
  }

  const response = await fetch(
    `${CHATBOT_BASE_URL}${CHATBOT_PATHS.sessions}/${sessionId}/prewarm`,
    {
      signal: params.signal,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        ...(params.enableWebSearch && { enable_web_search: true }),
        ...(params.projectId != null && { project_id: params.projectId }),
      }),
    }
  );

  const responseBody = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(responseBody?.detail || `Failed to prewarm session: ${response.status}`);
  }

  return {
    sessionId: responseBody?.session_id ?? sessionId,
    status: responseBody?.status ?? "failed",
    historyReady: Boolean(responseBody?.history_ready),
    reason: responseBody?.reason ?? null,
  };
}

/**
 * Fetch messages for a session with pagination
 * @throws {Error} If the request fails
 */
export async function fetchSessionMessages(
  sessionId: string,
  limit = 50,
  offset = 0,
  signal?: AbortSignal
): Promise<SessionMessagesResponse> {
  if (!CHATBOT_BASE_URL) {
    throw new Error("Chatbot URL is not configured. Set VITE_CHATBOT_URL to continue.");
  }

  const {
    data: { session },
  } = await sessionForRequest(signal);

  if (!session?.access_token) {
    throw new Error("Authentication required");
  }

  const url = new URL(`${CHATBOT_BASE_URL}${CHATBOT_PATHS.sessions}/${sessionId}/messages`);
  url.searchParams.set("limit", String(limit));
  url.searchParams.set("offset", String(offset));

  const response = await fetch(url.toString(), {
    signal,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody?.detail || `Failed to fetch messages: ${response.status}`);
  }

  const data = await response.json();

  return {
    messages: ((data.messages as RawSessionMessage[] | undefined) || []).map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content,
      createdAt: m.created_at,
      feedbackRating: m.feedback_rating ?? null,
      sourceFilenames: m.source_filenames ?? [],
      choices: m.choices ?? [],
      choiceQuestion: m.choice_question ?? "",
      usedDocumentTool: m.used_document_tool ?? false,
      ehrLink: m.ehr_link ?? null,
    })),
    total: data.total,
    hasMore: data.has_more,
  };
}

/**
 * Submit feedback for a chat message
 * @throws {Error} If the request fails
 */
export async function submitFeedback(params: {
  messageId: string;
  rating: FeedbackRating;
  comment?: string;
}): Promise<FeedbackResult> {
  if (!CHATBOT_BASE_URL) {
    throw new Error("Chatbot URL is not configured. Set VITE_CHATBOT_URL to continue.");
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Authentication required");
  }

  const response = await fetch(
    `${CHATBOT_BASE_URL}${CHATBOT_PATHS.messages}/${params.messageId}/feedback`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        rating: params.rating,
        comment: params.comment,
      }),
    }
  );

  let responseBody: RawFeedbackResponse | null = null;
  try {
    responseBody = await response.json();
  } catch {
    responseBody = null;
  }

  if (!response.ok) {
    const detail =
      responseBody?.detail ||
      responseBody?.message ||
      `Feedback request failed with status ${response.status}`;
    throw new Error(detail);
  }

  return {
    id: responseBody?.id,
    messageId: responseBody?.message_id ?? params.messageId,
    rating: responseBody?.rating ?? params.rating,
    comment: responseBody?.comment ?? null,
    updatedAt: responseBody?.updated_at ?? null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Chatbot attachments panel (WHA-3380)
//
// The ChatbotFilesPanel component used to query `public.documents` and set up
// a Realtime subscription directly from the component. Moved here to honour
// the CLAUDE.md "use the repository pattern" convention and so the supabase-js
// types flow through naturally (no `any` cast).
// ─────────────────────────────────────────────────────────────────────────────

/** Row shape the ChatbotFilesPanel renders. Mirrors the public.documents
 *  columns the panel needs (id, filename, file_path, …) plus the chatbot-
 *  specific `created_by_chatbot` flag that splits the two sections. */
export interface ChatbotAttachment {
  id: number;
  filename: string;
  file_path: string;
  content_type: string;
  size: number | null;
  content_hash: string | null;
  created_at: string | null;
  created_by_chatbot: boolean;
  state: string;
}

/** Resolve the per-building hidden chatbot project + fetch the current user's
 *  attachments under it. Returns the rows ready for render (newest first,
 *  cap 200). */
export async function fetchChatbotAttachments(params: {
  orgId: string;
  buildingId: string;
  userId: number;
}): Promise<ChatbotAttachment[]> {
  const projectRpc = await supabase.rpc("ensure_chatbot_attachments_project", {
    p_org_id: params.orgId,
    p_building_id: parseInt(params.buildingId, 10),
    p_user_id: params.userId,
  });
  if (projectRpc.error) throw projectRpc.error;
  const projectRow = Array.isArray(projectRpc.data) ? projectRpc.data[0] : projectRpc.data;
  if (!projectRow) return [];

  const { data, error } = await supabase
    .from("documents")
    .select(
      "id, filename, file_path, content_type, size, content_hash, created_at, created_by_chatbot, state"
    )
    .eq("project_id", projectRow.project_id)
    .eq("created_by", params.userId)
    .eq("is_deleted", false)
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as ChatbotAttachment[];
}

/** Subscribe to inserts/updates/deletes on `public.documents` rows belonging
 *  to `userId` so the panel can invalidate its query when a new attachment
 *  lands (drag-and-drop upload OR a Franklin-generated PDF from the chatbot
 *  service). Returns a cleanup function the caller invokes on unmount. */
export function subscribeChatbotAttachments(params: {
  buildingId: string;
  userId: number;
  onChange: () => void;
}): () => void {
  const channel = supabase
    .channel(`chatbot-files-panel-${params.buildingId}-${params.userId}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "documents",
        filter: `created_by=eq.${params.userId}`,
      },
      () => params.onChange()
    )
    .subscribe();
  return () => {
    void supabase.removeChannel(channel);
  };
}

/**
 * Delete feedback for a chat message
 * @throws {Error} If the request fails
 */
export async function deleteFeedback(messageId: string): Promise<void> {
  if (!CHATBOT_BASE_URL) {
    throw new Error("Chatbot URL is not configured. Set VITE_CHATBOT_URL to continue.");
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Authentication required");
  }

  const response = await fetch(
    `${CHATBOT_BASE_URL}${CHATBOT_PATHS.messages}/${messageId}/feedback`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    }
  );

  if (!response.ok) {
    let detail = `Failed to delete feedback: ${response.status}`;
    try {
      const errorBody = await response.json();
      detail = errorBody?.detail || detail;
    } catch {
      // ignore parse errors
    }
    throw new Error(detail);
  }
}
